/**
 * Conversion between the LyX AST (lyx/ast.ts) and ProseMirror JSON (schema.ts).
 * Both directions are lossless for everything the AST represents.
 */
import type {
  Change, FontState, Inset, Item, LyxDocument, Paragraph, TabularInset, TabularCell,
} from './lyx/ast.ts';
import { FONT_KEYS } from './lyx/ast.ts';
import {
  PAGE_LAYOUT, GROUP_LAYOUT, PAGE_PROPS_INSET, NOTES_INSET, parseBoxKeys, parseShapeKeys, parseImageKeys, parseRawKeys, parseGroupKeys, parsePageKeys,
  writeBoxKeys, writeShapeKeys, writeImageKeys, writeRawKeys, writeGroupKeys, writePageKeys, type PageProps,
} from './layout/model.ts';
import { schema } from './schema.ts';
import type { Node as PMNode } from 'prosemirror-model';

export interface PMJSON { type: string; attrs?: Record<string, any>; content?: PMJSON[]; marks?: { type: string; attrs?: Record<string, any> }[]; text?: string }

/* ----------------------------------------------------------------- LyX -> PM */

function marksFor(font: FontState, change?: Change): PMJSON['marks'] {
  const marks: NonNullable<PMJSON['marks']> = [];
  for (const k of FONT_KEYS) if (font[k] !== undefined) marks.push({ type: k, attrs: { value: font[k] } });
  if (change) marks.push({ type: 'change', attrs: { type: change.type, author: change.author, time: change.time } });
  return marks.length ? marks : undefined;
}

export function paragraphToPm(p: Paragraph): PMJSON {
  const attrs: Record<string, any> = {
    layout: p.layout, depth: p.depth,
    align: p.params.align ?? null,
    noindent: !!p.params.noindent,
    labelwidthstring: p.params.labelwidthstring ?? null,
    spacing: p.params.paragraph_spacing ?? null,
    leftindent: p.params.leftindent ?? null,
    appendix: !!p.params.start_of_appendix,
    endChange: p.endChange ? JSON.stringify(p.endChange) : null,
  };
  const content: PMJSON[] = [];
  for (const it of p.items) {
    const n = itemToPm(it);
    if (n) content.push(n);
  }
  const node: PMJSON = { type: 'paragraph', attrs };
  if (content.length) node.content = content;
  return node;
}

export function paragraphsToPm(pars: Paragraph[], defaultLayout = 'Plain Layout'): PMJSON[] {
  if (!pars.length) return [{ type: 'paragraph', attrs: { layout: defaultLayout, depth: 0 } }];
  return pars.map(paragraphToPm);
}

function itemToPm(it: Item): PMJSON | null {
  const marks = marksFor(it.font, it.change);
  let node: PMJSON | null;
  switch (it.kind) {
    case 'text':
      if (!it.text) return null;
      node = { type: 'text', text: it.text };
      break;
    case 'special':
      node = { type: 'special', attrs: { token: it.token, arg: it.arg } };
      break;
    case 'unknown':
      node = { type: 'unknown', attrs: { line: it.line } };
      break;
    case 'inset':
      node = insetToPm(it.inset);
      break;
  }
  if (node && marks) {
    if (node.type === 'text') node.marks = marks;
    else node.attrs = { ...(node.attrs ?? {}), marks: JSON.stringify(marks) };
  }
  return node;
}

export function insetToPm(ins: Inset): PMJSON {
  switch (ins.type) {
    case 'Formula': {
      if (ins.inline) {
        const m = /^\$([\s\S]*)\$$/.exec(ins.latex);
        if (m) return { type: 'math_inline', attrs: { latex: m[1], delim: '$' } };
        return { type: 'math_inline', attrs: { latex: ins.latex, delim: '' } };
      }
      return { type: 'math_display', attrs: { latex: ins.latex } };
    }
    case 'FormulaMacro':
      return { type: 'macro', attrs: { lines: JSON.stringify(ins.lines) } };
    case 'Text':
      return {
        type: 'inset',
        attrs: { name: ins.name, arg: ins.arg, params: JSON.stringify(ins.params), status: ins.status ?? null },
        content: paragraphsToPm(ins.paragraphs),
      };
    case 'Leaf':
      switch (ins.name) {
        case 'CommandInset': return { type: 'command', attrs: { cmd: ins.arg, params: JSON.stringify(ins.params) } };
        case 'Graphics': return { type: 'graphics', attrs: { params: JSON.stringify(ins.params) } };
        case 'Quotes': return { type: 'quotes', attrs: { kind: ins.arg } };
        case 'Sketch': return { type: 'sketch', attrs: { src: ins.arg, data: ins.params[0] ?? null } };
        case 'space': return { type: 'space', attrs: { kind: ins.arg, params: JSON.stringify(ins.params) } };
        case 'Newline': return { type: 'newline', attrs: { kind: ins.arg } };
        case 'Newpage': return { type: 'newpage', attrs: { kind: ins.arg } };
        default: return { type: 'leaf', attrs: { name: ins.name, arg: ins.arg, params: JSON.stringify(ins.params) } };
      }
    case 'Tabular':
      return tabularToPm(ins);
    case 'Raw':
      return { type: 'raw', attrs: { firstLine: ins.firstLine, lines: JSON.stringify(ins.lines) } };
  }
}

function attrGet(attrs: [string, string][], key: string): string | undefined {
  for (const [k, v] of attrs) if (k === key) return v;
  return undefined;
}

function tabularToPm(t: TabularInset): PMJSON {
  const ncols = t.columns.length || Number(attrGet(t.attrs, 'columns') || 0);
  const rows: PMJSON[] = [];
  // occupancy grid for multirow continuation
  const occupied: (PMJSON | null)[][] = t.rows.map(() => new Array(ncols).fill(null));
  for (let r = 0; r < t.rows.length; r++) {
    const row = t.rows[r];
    const cells: PMJSON[] = [];
    let lastStart: PMJSON | null = null;
    for (let c = 0; c < row.cells.length; c++) {
      const cell = row.cells[c];
      const mc = attrGet(cell.attrs, 'multicolumn');
      const mr = attrGet(cell.attrs, 'multirow');
      const isCont = mc === '2' || mr === '2';
      if (isCont) {
        // attach to the start cell (multicolumn: previous start in this row; multirow: cell above)
        let start: PMJSON | null = null;
        if (mr === '2') { for (let rr = r - 1; rr >= 0 && !start; rr--) start = occupied[rr][c]; }
        if (!start) start = lastStart;
        if (start) {
          const cont: [string, string][][] = JSON.parse(start.attrs!.cont);
          cont.push(cell.attrs);
          start.attrs!.cont = JSON.stringify(cont);
          if (mr === '2') {
            start.attrs!.rowspan = (start.attrs!.rowspan || 1) + 1;
            if (occupied[r]) occupied[r][c] = start;
          } else {
            start.attrs!.colspan = (start.attrs!.colspan || 1) + 1;
          }
          // keep content of continuation cells (normally empty) to be lossless
          const paras = cell.paragraphs.filter(p => p.items.length);
          if (paras.length) {
            const extra: PMJSON[][] = JSON.parse(start.attrs!.contContent || '[]');
            extra.push(cell.paragraphs.map(paragraphToPm));
            start.attrs!.contContent = JSON.stringify(extra);
          }
        }
        continue;
      }
      const node: PMJSON = {
        type: 'table_cell',
        attrs: { colspan: 1, rowspan: 1, colwidth: null, attrs: JSON.stringify(cell.attrs), cont: '[]' },
        content: paragraphsToPm(cell.paragraphs),
      };
      if (occupied[r]) occupied[r][c] = node;
      cells.push(node);
      lastStart = node;
    }
    rows.push({ type: 'table_row', attrs: { attrs: JSON.stringify(row.attrs) }, content: cells });
  }
  return {
    type: 'table',
    attrs: { attrs: JSON.stringify(t.attrs), features: JSON.stringify(t.features), columns: JSON.stringify(t.columns.map(c => c.attrs)) },
    content: rows,
  };
}

export function lyxToPm(doc: LyxDocument): PMJSON {
  if (!doc.body.length) return { type: 'doc', content: [{ type: 'paragraph', attrs: { layout: 'Standard', depth: 0 } }] };
  // a layout document: pages only (the schema's doc is paragraphs or pages); anything between
  // its pages is kept as unplaced raw LaTeX on the page before (or after, before the first)
  if (doc.body.some(p => p.layout === PAGE_LAYOUT)) return { type: 'doc', content: layoutBodyToPm(doc.body) };
  return { type: 'doc', content: doc.body.map(paragraphToPm) };
}

/* ----------------------------------------------------------------- layout pages */

function keysOf(params: string[]): string {
  for (const l of params) if (l === 'keys' || l.startsWith('keys ')) return l.slice(5);
  return '';
}
function paramOf(params: string[], key: string): string | undefined {
  for (const l of params) { if (l === key) return ''; if (l.startsWith(key + ' ')) return l.slice(key.length + 1); }
  return undefined;
}

function layoutBodyToPm(body: Paragraph[]): PMJSON[] {
  const pages: PMJSON[] = [];
  let stray: PMJSON[] = [];
  for (const p of body) {
    if (p.layout === PAGE_LAYOUT) {
      const page = pageToPm(p);
      if (stray.length) { page.content = [...stray, ...(page.content ?? [])]; stray = []; }
      pages.push(page);
      continue;
    }
    // text outside any page (a hand-edited file): unplaced raw LaTeX, written back where it was
    const latex = paragraphText(p);
    if (!latex.trim()) continue;
    const raw: PMJSON = { type: 'ol_raw', attrs: { latex, placed: false } };
    const last = pages[pages.length - 1];
    if (last) insertBeforeNotes(last, raw); else stray.push(raw);
  }
  if (stray.length) pages.push({ type: 'ol_page', attrs: {}, content: stray });
  return pages;
}
function insertBeforeNotes(page: PMJSON, node: PMJSON): void {
  const c = page.content ?? (page.content = []);
  const notes = c.length && c[c.length - 1].type === 'ol_notes';
  c.splice(notes ? c.length - 1 : c.length, 0, node);
}
/** a paragraph outside the pages as LaTeX-ish text (only its raw material survives) */
function paragraphText(p: Paragraph): string {
  return p.items.map(it => it.kind === 'text' ? it.text : it.kind === 'inset' && it.inset.type === 'Text' && it.inset.name === 'ERT'
    ? it.inset.paragraphs.map(q => q.items.map(i => i.kind === 'text' ? i.text : '').join('')).join('\n') : '').join('');
}

export function pageToPm(p: Paragraph): PMJSON {
  let attrs: Record<string, any> = {};
  const content: PMJSON[] = [];
  let notes: PMJSON | null = null;
  for (const it of p.items) {
    if (it.kind !== 'inset') continue;
    const ins = it.inset;
    if (ins.type === 'Leaf' && ins.name === PAGE_PROPS_INSET) {
      attrs = { ...parsePageKeys(keysOf(ins.params), paramOf(ins.params, 'frame') ?? 'plain') };
      if (paramOf(ins.params, 'role') === 'master') attrs.role = 'master';
      continue;
    }
    if (ins.type === 'Text' && ins.name === NOTES_INSET) { notes = { type: 'ol_notes', content: paragraphsToPm(ins.paragraphs) }; continue; }
    const o = objectToPm(it);
    if (o) content.push(o);
  }
  if (notes) content.push(notes);
  return { type: 'ol_page', attrs, content };
}

function objectToPm(it: Item): PMJSON | null {
  if (it.kind !== 'inset') return null;
  const ins = it.inset;
  if (ins.type === 'Text' && ins.name === 'OLBox') return { type: 'ol_box', attrs: { ...parseBoxKeys(keysOf(ins.params)) }, content: paragraphsToPm(ins.paragraphs) };
  if (ins.type === 'Text' && ins.name === 'OLGroup') {
    const children = ins.paragraphs.flatMap(q => q.items.map(objectToPm).filter((x): x is PMJSON => !!x));
    if (!children.length) return null;
    return { type: 'ol_group', attrs: { ...parseGroupKeys(keysOf(ins.params)) }, content: children };
  }
  if (ins.type !== 'Leaf') return null;
  const keys = keysOf(ins.params);
  if (ins.name === 'OLShape') return { type: 'ol_shape', attrs: { ...parseShapeKeys(keys), d: paramOf(ins.params, 'path') ?? '' } };
  if (ins.name === 'OLImage') {
    const { crop, ...rest } = parseImageKeys(keys);
    return { type: 'ol_image', attrs: { ...rest, cl: crop[0], ct: crop[1], cr: crop[2], cb: crop[3], src: paramOf(ins.params, 'src') ?? '' } };
  }
  if (ins.name === 'OLRaw') {
    let latex = '';
    try { latex = JSON.parse(paramOf(ins.params, 'latex') ?? '""'); } catch { /* damaged */ }
    const placed = paramOf(ins.params, 'placed') !== 'false';
    return { type: 'ol_raw', attrs: { ...parseRawKeys(keys, placed), latex, placed } };
  }
  return null;
}

const objItem = (inset: Inset): Item => ({ kind: 'inset', font: {}, inset });

export function pmPageToLyx(page: PMJSON): Paragraph {
  const a = (page.attrs ?? {}) as PageProps;
  const props: Inset = { type: 'Leaf', name: PAGE_PROPS_INSET, arg: '', params: ['keys ' + writePageKeys({ fill: a.fill ?? null, master: a.master ?? null, transition: a.transition ?? null, name: a.name ?? null, frame: a.frame ?? 'plain', extra: a.extra ?? '' }), `frame ${a.frame ?? 'plain'}`] };
  if ((a as { role?: string }).role === 'master') props.params.push('role master');
  const items: Item[] = [objItem(props)];
  for (const c of page.content ?? []) {
    if (c.type === 'ol_notes') { items.push(objItem({ type: 'Text', name: NOTES_INSET, arg: '', params: [], paragraphs: pmBlocksToParagraphs(c.content ?? []) })); continue; }
    const it = pmObjectToLyx(c);
    if (it) items.push(it);
  }
  return { layout: PAGE_LAYOUT, depth: 0, params: {}, items };
}

function withDefaults<T>(a: Record<string, any> | undefined, defs: T): T {
  const out: any = { ...defs };
  for (const k of Object.keys(defs as object)) if (a && a[k] !== undefined) out[k] = a[k];
  return out;
}

function pmObjectToLyx(c: PMJSON): Item | null {
  const a = c.attrs ?? {};
  switch (c.type) {
    case 'ol_box':
      return objItem({ type: 'Text', name: 'OLBox', arg: '', params: ['keys ' + writeBoxKeys(withDefaults(a, parseBoxKeys('')))], paragraphs: pmBlocksToParagraphs(c.content ?? []) });
    case 'ol_group': {
      const children = (c.content ?? []).map(pmObjectToLyx).filter((x): x is Item => !!x);
      return objItem({ type: 'Text', name: 'OLGroup', arg: '', params: ['keys ' + writeGroupKeys(withDefaults(a, parseGroupKeys('')))], paragraphs: [{ layout: GROUP_LAYOUT, depth: 0, params: {}, items: children }] });
    }
    case 'ol_shape':
      return objItem({ type: 'Leaf', name: 'OLShape', arg: '', params: ['keys ' + writeShapeKeys(withDefaults(a, parseShapeKeys(''))), 'path ' + String(a.d ?? '')] });
    case 'ol_image': {
      const p = withDefaults(a, parseImageKeys(''));
      p.crop = [Number(a.cl) || 0, Number(a.ct) || 0, Number(a.cr) || 0, Number(a.cb) || 0];
      return objItem({ type: 'Leaf', name: 'OLImage', arg: '', params: ['keys ' + writeImageKeys(p), 'src ' + String(a.src ?? '')] });
    }
    case 'ol_raw': {
      const placed = a.placed !== false;
      const params = ['keys ' + (placed ? writeRawKeys(withDefaults(a, parseRawKeys(''))) : ''), 'latex ' + JSON.stringify(String(a.latex ?? ''))];
      if (!placed) params.push('placed false');
      return objItem({ type: 'Leaf', name: 'OLRaw', arg: '', params });
    }
    default:
      return null;
  }
}

/* ----------------------------------------------------------------- PM -> LyX */

function fontFromMarks(marks: PMJSON['marks'] | readonly any[] | undefined): { font: FontState; change?: Change } {
  const font: FontState = {};
  let change: Change | undefined;
  for (const m of marks ?? []) {
    const type = typeof m.type === 'string' ? m.type : m.type.name;
    const attrs = m.attrs ?? {};
    if (type === 'change') change = { type: attrs.type, author: Number(attrs.author), time: Number(attrs.time) };
    else if ((FONT_KEYS as readonly string[]).includes(type)) (font as any)[type] = attrs.value;
  }
  return change ? { font, change } : { font };
}

function toJSON(n: PMNode | PMJSON): PMJSON {
  return (n as any).toJSON ? (n as PMNode).toJSON() : (n as PMJSON);
}

export function pmParagraphToLyx(nj: PMJSON): Paragraph {
  const a = nj.attrs ?? {};
  const p: Paragraph = { layout: a.layout ?? 'Standard', depth: a.depth ?? 0, params: {}, items: [] };
  if (a.align) p.params.align = a.align;
  if (a.noindent) p.params.noindent = true;
  if (a.labelwidthstring) p.params.labelwidthstring = a.labelwidthstring;
  if (a.spacing) p.params.paragraph_spacing = a.spacing;
  if (a.leftindent) p.params.leftindent = a.leftindent;
  if (a.appendix) p.params.start_of_appendix = true;
  if (a.endChange) { try { p.endChange = JSON.parse(a.endChange); } catch { /* ignore */ } }
  for (const c of nj.content ?? []) {
    const it = pmInlineToItem(c);
    if (!it) continue;
    const last = p.items[p.items.length - 1];
    if (it.kind === 'text' && last && last.kind === 'text' && sameFont(last, it)) last.text += it.text;
    else p.items.push(it);
  }
  return p;
}

function sameFont(a: Item, b: Item): boolean {
  for (const k of FONT_KEYS) if (a.font[k] !== b.font[k]) return false;
  const ca = a.change, cb = b.change;
  if (!ca && !cb) return true;
  if (!ca || !cb) return false;
  return ca.type === cb.type && ca.author === cb.author && ca.time === cb.time;
}

function pmInlineToItem(c: PMJSON): Item | null {
  const a = c.attrs ?? {};
  const parse = (s: string | undefined, def: any) => { try { return s ? JSON.parse(s) : def; } catch { return def; } };
  // marks on non-text nodes live in the `marks` attribute (see schema.ts); PM marks are merged in too
  const fc = c.type === 'text' ? fontFromMarks(c.marks) : fontFromMarks([...(parse(a.marks, []) as any[]), ...(c.marks ?? [])]);
  switch (c.type) {
    case 'text':
      return c.text ? { kind: 'text', text: c.text, ...fc } : null;
    case 'special':
      return { kind: 'special', token: a.token, arg: a.arg, ...fc };
    case 'unknown':
      return { kind: 'unknown', line: a.line, ...fc };
    case 'math_inline':
      return { kind: 'inset', inset: { type: 'Formula', inline: true, latex: a.delim === '$' ? '$' + a.latex + '$' : a.latex }, ...fc };
    case 'math_display':
      return { kind: 'inset', inset: { type: 'Formula', inline: false, latex: a.latex }, ...fc };
    case 'macro':
      return { kind: 'inset', inset: { type: 'FormulaMacro', lines: parse(a.lines, []) }, ...fc };
    case 'inset':
      return {
        kind: 'inset', ...fc,
        inset: { type: 'Text', name: a.name, arg: a.arg ?? '', params: parse(a.params, []), status: a.status ?? undefined, paragraphs: pmBlocksToParagraphs(c.content ?? []) },
      };
    case 'command':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'CommandInset', arg: a.cmd, params: parse(a.params, []) }, ...fc };
    case 'graphics':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'Graphics', arg: '', params: parse(a.params, []) }, ...fc };
    case 'quotes':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'Quotes', arg: a.kind, params: [] }, ...fc };
    case 'sketch':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'Sketch', arg: a.src ?? '', params: a.data != null ? [a.data] : [] }, ...fc };
    case 'space':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'space', arg: a.kind, params: parse(a.params, []) }, ...fc };
    case 'newline':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'Newline', arg: a.kind, params: [] }, ...fc };
    case 'newpage':
      return { kind: 'inset', inset: { type: 'Leaf', name: 'Newpage', arg: a.kind, params: [] }, ...fc };
    case 'leaf':
      return { kind: 'inset', inset: { type: 'Leaf', name: a.name, arg: a.arg ?? '', params: parse(a.params, []) }, ...fc };
    case 'raw':
      return { kind: 'inset', inset: { type: 'Raw', firstLine: a.firstLine, lines: parse(a.lines, []) }, ...fc };
    case 'table':
      return { kind: 'inset', inset: pmTableToLyx(c), ...fc };
    default:
      return null;
  }
}

export function pmBlocksToParagraphs(blocks: PMJSON[]): Paragraph[] {
  const out: Paragraph[] = [];
  for (const b of blocks) {
    if (b.type === 'paragraph') out.push(pmParagraphToLyx(b));
    else if (b.type === 'ol_page') out.push(pmPageToLyx(b));
    else {
      // any other block (should not happen with our schema) — wrap as a Plain Layout paragraph
      const it = pmInlineToItem(b);
      const p: Paragraph = { layout: 'Plain Layout', depth: 0, params: {}, items: it ? [it] : [] };
      out.push(p);
    }
  }
  return out;
}

function pmTableToLyx(t: PMJSON): TabularInset {
  const parse = (s: string | undefined, def: any) => { try { return s ? JSON.parse(s) : def; } catch { return def; } };
  const a = t.attrs ?? {};
  const columns: [string, string][][] = parse(a.columns, []);
  const rowsIn = t.content ?? [];
  // grid reconstruction with spans
  const nrows = rowsIn.length;
  let ncols = columns.length;
  // compute actual column count from content if columns attr is missing/short
  {
    const widths = rowsIn.map(r => (r.content ?? []).reduce((s, c) => s + (c.attrs?.colspan || 1), 0));
    ncols = Math.max(ncols, ...widths, 0);
  }
  const grid: ({ start: PMJSON; cont?: [string, string][]; contContent?: PMJSON[] } | null)[][] = Array.from({ length: nrows }, () => new Array(ncols).fill(null));
  for (let r = 0; r < nrows; r++) {
    const cells = rowsIn[r].content ?? [];
    let c = 0;
    for (const cell of cells) {
      while (c < ncols && grid[r][c]) c++;
      if (c >= ncols) break;
      const cs = cell.attrs?.colspan || 1, rs = cell.attrs?.rowspan || 1;
      const cont: [string, string][][] = parse(cell.attrs?.cont, []);
      const contContent: PMJSON[][] = parse(cell.attrs?.contContent, []);
      let k = 0;
      for (let dr = 0; dr < rs; dr++) for (let dc = 0; dc < cs; dc++) {
        if (r + dr >= nrows || c + dc >= ncols) continue;
        if (dr === 0 && dc === 0) grid[r][c] = { start: cell };
        else {
          const attrs = cont[k] ?? defaultContAttrs(cell, dc > 0, dr > 0);
          const cc = contContent[k];
          grid[r + dr][c + dc] = { start: cell, cont: attrs, contContent: cc };
          k++;
        }
      }
      c += cs;
    }
  }
  const rows = rowsIn.map((row, r) => {
    const cells: TabularCell[] = [];
    for (let c = 0; c < ncols; c++) {
      const g = grid[r][c];
      if (!g) {
        cells.push({ attrs: [['alignment', 'center'], ['valignment', 'top'], ['usebox', 'none']], paragraphs: [{ layout: 'Plain Layout', depth: 0, params: {}, items: [] }] });
      } else if (g.cont) {
        cells.push({ attrs: g.cont, paragraphs: g.contContent ? pmBlocksToParagraphs(g.contContent) : [{ layout: 'Plain Layout', depth: 0, params: {}, items: [] }] });
      } else {
        // a span's first cell carries multicolumn/multirow="1" in LyX (cells pasted from HTML lack it)
        let attrs: [string, string][] = parse(g.start.attrs?.attrs, []);
        const cs = g.start.attrs?.colspan || 1, rs = g.start.attrs?.rowspan || 1;
        if (rs > 1 && !attrs.some(x => x[0] === 'multirow')) attrs = [['multirow', '1'], ...attrs];
        if (cs > 1 && !attrs.some(x => x[0] === 'multicolumn')) attrs = [['multicolumn', '1'], ...attrs];
        // LyX allows only Plain Layout inside table cells
        const paragraphs = pmBlocksToParagraphs(g.start.content ?? []).map(p => (p.layout === 'Standard' ? { ...p, layout: 'Plain Layout' } : p));
        cells.push({ attrs, paragraphs });
      }
    }
    return { attrs: parse(row.attrs?.attrs, []), cells };
  });
  const tattrs: [string, string][] = parse(a.attrs, []);
  const setA = (k: string, v: string) => { const i = tattrs.findIndex(x => x[0] === k); if (i >= 0) tattrs[i] = [k, v]; else tattrs.push([k, v]); };
  if (!tattrs.length) setA('version', '3');
  setA('rows', String(nrows)); setA('columns', String(ncols));
  const cols = columns.slice(0, ncols);
  // a table that did not come from a LyX file (pasted HTML) may lack column definitions
  while (cols.length < ncols) cols.push([['alignment', 'center'], ['valignment', 'top']]);
  return { type: 'Tabular', attrs: tattrs, features: parse(a.features, []), columns: cols.map(attrs => ({ attrs })), rows };
}

function defaultContAttrs(start: PMJSON, mc: boolean, mr: boolean): [string, string][] {
  const base: [string, string][] = JSON.parse(start.attrs?.attrs ?? '[]');
  const out: [string, string][] = [];
  if (mc) out.push(['multicolumn', '2']);
  if (mr) out.push(['multirow', '2']);
  for (const [k, v] of base) if (k !== 'multicolumn' && k !== 'multirow' && k !== 'mroffset') out.push([k, v]);
  return out;
}

/** Convert a ProseMirror document (JSON or Node) to LyX paragraphs. */
export function pmToLyxBody(doc: PMNode | PMJSON): Paragraph[] {
  const j = toJSON(doc);
  return pmBlocksToParagraphs(j.content ?? []);
}

/** Build a ProseMirror Node from a LyX document (validated against the schema). */
export function lyxToPmNode(doc: LyxDocument): PMNode {
  const node = schema.nodeFromJSON(lyxToPm(doc));
  node.check();
  return node;
}
